import React, { Dispatch, useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';
import { Action } from 'redux';

import lang from '../../../../lib/language';
import { TopGainerAndLoserData } from '../../../../lib/models/fundamental/types';
import { RootState } from '../../../../lib/store';
import { loadDiscoverChartData } from '../../../../lib/store/common-actions';
import { FundamentalCall } from '../../../../lib/store/fundamental/types';
import { getLimitedText } from '../../../../lib/util/DataHelpers';
import { isCallStatusReady } from '../../../../lib/util/error-handling/StatusByCallHelpers';
import { manageSubscriptions } from '../../../../lib/util/TradingHelpers';

import { BaseTable, CompanyLogo } from '../../../../components';
import ClickableDiv from '../../../../components/ClickableDiv/ClickableDiv';
import { useIsMobile } from '../../../../hooks';
import { UI_ROUTES } from '../../../../util/routes';
import {
  EPIC_MOVERS_ROW_COUNT,
  EPIC_MOVERS_SYMBOL_NAME_LIMIT,
  EPIC_MOVERS_TABLE_HEADERS,
} from '../../EpicMovers.constants';
import PriceAndChange from '../PriceAndChange/PriceAndChange';

import EpicMoversLoaderRow from './EpicMoversLoaderRow';

import styles from '../../EpicMovers.module.scss';

type OwnProps = { data: TopGainerAndLoserData[], title: string, isReduction: boolean, isTopGainersSelected: boolean }
type Props = OwnProps & ReduxProps;

const EpicMoversList = ({ companyLogoAndName, getChartData, isEpicMoversReady, data, isReduction,
  title, isTopGainersSelected }: Props) => {
  const isMobile = useIsMobile();
  const history = useHistory();
  const tableHeaders1 = [title, ''].map((item: string) => <th key={item}>{item}</th>);
  const tableHeaders2 = EPIC_MOVERS_TABLE_HEADERS.map((item: string) => <th key={item}>{item}</th>);
  const MINIMUM_RESULTS = 4;

  const isLoading = !isEpicMoversReady || !companyLogoAndName || data.length === 0;

  const renderList = () => {
    const results = data.slice(0, EPIC_MOVERS_ROW_COUNT).map((item: TopGainerAndLoserData) => {
      const logo = companyLogoAndName[item.symbol]?.logo;
      const { url } = item;
      const displayName = getLimitedText(companyLogoAndName[item.symbol]?.name, EPIC_MOVERS_SYMBOL_NAME_LIMIT);

      if (isMobile) {
        return (
          <ClickableDiv
            key={item.symbol}
            onClick={() => history.push(`${UI_ROUTES.symbol}/${item.symbol}`)}
            className={styles.wrapperCompanyMob}
          >
            <div className={styles.left}>
              <div className={styles.logoWrapper}>
                {(url || logo) && !isLoading ? (
                  <CompanyLogo url={url || ''} logo={logo || ''} symbol={item.symbol} />
                ) : (
                  <div className={styles.loadingLogo} />
                )}
              </div>
              <div className={styles.companyInfo}>
                <p className={styles.symbol}>{item.symbol}</p>
                <p className={styles.symbolDisplayName}>
                  {displayName}
                </p>
              </div>
            </div>
            <div className={styles.right}>
              <PriceAndChange item={item} isReduction={isReduction} isLoading={!isEpicMoversReady} />
            </div>
          </ClickableDiv>
        );
      }

      return (
        <tr
          className={styles.tableRow}
          key={item.symbol}
          onClick={() => history.push(`${UI_ROUTES.symbol}/${item.symbol}`)}
        >
          <td>
            <div className={styles.wrapperCompany}>
              {(url || logo) && isEpicMoversReady ? (
                <CompanyLogo url={url || ''} logo={logo || ''} symbol={item.symbol} />
              ) : (
                <div className={styles.loadingLogo} />
              )}
              <div className={styles.companyInfo}>
                {!item.symbol || !displayName ? (
                  <>
                    <div className={classNames(styles.loadingComponent, styles.loadingComponentMargin)} />
                    <div className={styles.loadingComponent} style={{ width: 134 }} />
                  </>
                ) : (
                  <>
                    <p className={styles.symbol}>{item.symbol}</p>
                    <p className={styles.symbolDisplayName}>{displayName}</p>
                  </>
                )}
              </div>
            </div>
          </td>
          <td><PriceAndChange item={item} isReduction={isReduction} isLoading={!isEpicMoversReady} /></td>
        </tr>
      );
    });

    while (results.length < MINIMUM_RESULTS) {
      results.push(
        <div className={styles.emptyCompanyField} />,
      );
    }

    return results;
  };

  useEffect(() => {
    if (isEpicMoversReady) {
      const epicMoversSymbols = data?.map(item => item.symbol).join(',');
      getChartData(epicMoversSymbols);
      manageSubscriptions('discover/epic-movers');
    }
  }, [isEpicMoversReady]);// eslint-disable-line react-hooks/exhaustive-deps

  if (isMobile) {
    return (
      <>
        { isLoading || data.length === 0 ? (
          <BaseTable className={styles.table}>
            <EpicMoversLoaderRow />
            <EpicMoversLoaderRow />
            <EpicMoversLoaderRow />
            <EpicMoversLoaderRow />
          </BaseTable>
        ) : (
          <div className={styles.tableMobile}>
            <div className={styles.tableHeaders}>
              <div>{EPIC_MOVERS_TABLE_HEADERS[0]}</div>
              <div>{EPIC_MOVERS_TABLE_HEADERS[1]}</div>
            </div>
            {renderList()}
            <ClickableDiv
              className={styles.viewAllBtnMob}
              disabled={!isEpicMoversReady}
              onClick={() => history.push({
                pathname: UI_ROUTES.discover + UI_ROUTES.epicMoversToday,
                state: { isTopGainersSelected },
              })}
            >
              {lang.mobileFundsScreenViewAllBtn()}
            </ClickableDiv>
          </div>
        )}
      </>
    );
  }

  return (
    <BaseTable className={styles.table}>
      {!isMobile && (
        <thead>
          <tr className={styles.tableMainHeader}>{tableHeaders1}</tr>
          <tr>{tableHeaders2}</tr>
        </thead>
      )}
      <tbody>
        {isLoading || data.length === 0 ? (
          <>
            <EpicMoversLoaderRow />
            <EpicMoversLoaderRow />
            <EpicMoversLoaderRow />
            <EpicMoversLoaderRow />
          </>
        ) : (
          <>
            {renderList()}
          </>
        )}
      </tbody>
    </BaseTable>

  );
};

const mapStateToProps = (state: RootState) => ({
  isEpicMoversReady: isCallStatusReady(FundamentalCall.getEpicMovers, state.fundamental.statusByCall),
  companyLogoAndName: state.fundamental.companyLogoAndName,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  getChartData: (symbols: string) => dispatch(loadDiscoverChartData(symbols)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(EpicMoversList);
